import React from 'react';
import { useGlobalValue } from '../../../state/GlobalProvider';
import { createLink } from '../../../utils';
import styles from './EntityCard.module.sass';
import Checkbox from '../../inputs/Checkbox/Checkbox';
import Icon from '../../Icon/Icon';
import LinkButton from '../../LinkButton/LinkButton';
import { UserRoles } from '../../../utils/userRoles';
import { checkRoles } from '../../../utils/roleValidator';

const renderRowActions = (actions, fileId, handleChangeCheckbox, checked) => {
  return actions.map((actionName, index) => {
    switch (actionName) {
      case 'delete':
        {
          return /*#__PURE__*/React.createElement(Checkbox, {
            key: fileId,
            name: "delete",
            value: fileId,
            checked: checked,
            onChange: e => handleChangeCheckbox(fileId, e.target.checked),
            onClick: e => e.stopPropagation()
          });
        }

      default:
        {
          return null;
        }
    }
  });
};

const EntityCard = props => {
  // TODO: use prop filter to highlight corresponding row
  const {
    data,
    checkbox,
    handleChangeCheckbox
  } = props;
  const {
    proxy,
    params,
    user,
    downloadFile,
    hideFile,
    deleteInvalidFilePermanently,
    pushDialog,
    i18n,
    history,
    sendSAP,
    downloadJSON,
    deleteDataDictionaryRow,
    reload
  } = useGlobalValue();
  const item = Array.isArray(data) ? data : (data || {}).card || [];
  let rowStatus = null;
  const columns = item.map((column, index) => {
    if (!column) {
      return null;
    }

    let alignment = '';
    let columnWidth = null;
    let flexBasis = null;
    const rows = column.map((row, rowIndex) => {
      if (!row) {
        return null;
      }

      const {
        type,
        value,
        style,
        align,
        mobileHidden,
        basis,
        classStyleName
      } = row;
      flexBasis = basis || flexBasis;
      let valueElement;

      switch (type) {
        case 'number':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;
            valueElement = value && value != 'null' || value == 0 ? value : "N/A";
            break;
          }

        case 'unity':
          {
            alignment = alignment !== 'align-right' ? 'align-right' : alignment;
            valueElement = value || "N/A";
            break;
          }

        case 'percentage':
          {
            alignment = alignment !== 'align-right' ? 'align-right' : alignment;

            if (value === "N/A") {
              valueElement = value;
            } else {
              valueElement = `${value || 0}%`;
            }

            break;
          }

        case 'date':
        case 'datetime':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;
            valueElement = value || "N/A";
            break;
          }

        case 'date_interval':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;
            valueElement = value ? (value || []).join(' - ') : "N/A";
            break;
          }

        case 'currency':
          {
            alignment = alignment !== 'currency align-right' ? 'currency align-right' : alignment;
            valueElement = value || 0;
            break;
          }

        case 'status':
          {
            alignment = alignment !== `align-${value[2]}` ? `align-${value[2]}` : alignment;
            let statusClass;

            if (value[0] !== 'deleted') {
              switch (value[0]) {
                case "Audit Failed":
                  statusClass = 'failed-status';
                  break;

                case "Valid Audit":
                  statusClass = 'ok-status';
                  break;

                default:
                  statusClass = value[0];
                  break;
              }
            }

            valueElement = /*#__PURE__*/React.createElement("span", {
              className: `${styles['status-container']} ${styles[statusClass]}`
            }, /*#__PURE__*/React.createElement("strong", {
              className: `${styles['status']}`
            }, value[1] || "N/A"));
            break;
          }

        case 'fileStatus':
          {
            alignment = alignment !== `align-${value[2]}` ? `align-${value[2]}` : alignment;
            let statusClass;

            switch (value[0]) {
              case 'imported':
                statusClass = 'failed-status';
                break;

              case 1:
                statusClass = 'ok-status';
                break;

              default:
                statusClass = 'in_progress-status';
                break;
            }

            valueElement = /*#__PURE__*/React.createElement("span", {
              className: `${styles['status-container']} ${styles[statusClass]}`
            }, /*#__PURE__*/React.createElement("strong", {
              className: `${styles['status']}`
            }, value[1] || "N/A"));
            break;
          }

        case 'periods':
          {
            const slots = new Array(12).fill(false);
            value.forEach(p => {
              slots[p - 1] = true;
            });
            const blocksToDisplay = slots.filter((hasData, index) => index < 12 || index === 12 && hasData);
            columnWidth = 'periods-width';
            valueElement = /*#__PURE__*/React.createElement("div", {
              className: styles['periods']
            }, blocksToDisplay.map((hasData, index) => /*#__PURE__*/React.createElement("span", {
              key: index,
              className: styles['period']
            }, /*#__PURE__*/React.createElement("div", {
              className: `${styles['bullet']} ${hasData ? styles['filled'] : ''}`
            }), /*#__PURE__*/React.createElement("div", {
              className: styles['label']
            }, index < 12 ? index + 1 : '...'))));
            break;
          }

        case 'progressbar':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;
            let status = '';

            switch (value[0]) {
              case 'uploaded':
                status = 'uploaded';
                break;

              case 'in_progress':
              case 'valid':
                status = `in_progress_${value[2]}`;
                break;

              case 'invalid':
                status = `invalid_${value[2]}`;
                break;

              case 'error':
                status = `error_${value[2]}`;
                break;

              case 'imported':
                status = 'imported';
                break;

              default:
                break;
            }

            valueElement = /*#__PURE__*/React.createElement("ul", {
              className: `${styles['progressbar']} ${styles[status]}`
            }, /*#__PURE__*/React.createElement("li", null), /*#__PURE__*/React.createElement("li", null), /*#__PURE__*/React.createElement("li", null), /*#__PURE__*/React.createElement("li", null), /*#__PURE__*/React.createElement("li", null), /*#__PURE__*/React.createElement("li", null));
            break;
          }

        case 'stageFlow':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;
            let status;

            const getFlowType = flowState => {
              let statusFow = undefined;

              switch (flowState) {
                case 'PARSE':
                  statusFow = '2';
                  break;

                case 'KPI':
                  statusFow = '3';
                  break;

                case 'RULES':
                  statusFow = '4';
                  break;

                case 'MERGE':
                  statusFow = '5';
                  break;

                case 'READY':
                  statusFow = '6';
                  break;

                default:
                  break;
              }

              return statusFow;
            };

            switch (value[0]) {
              case 'uploaded':
                status = '1';
                break;

              case 'in_progress':
              case 'xsd_checking':
              case 'saving_to_db':
              case 'test_running':
              case 'checking_file':
              case 'in_progress':
              case 'valid':
                status = getFlowType(value[2]) || '2';
                break;

              case 'invalid':
                status = getFlowType(value[2]) || '2';
                break;

              case 'error':
                status = getFlowType(value[2]) || '2';
                break;

              case 'imported':
                status = '6';
                break;

              default:
                status = '0';
                break;
            }

            valueElement = /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("strong", {
              className: `${styles['status']}`
            }, status), "/6"));
            break;
          }

        case 'object':
          {
            alignment = alignment !== 'align-left' ? 'align-left' : alignment;

            if (value && value.id && value.name) {
              valueElement = /*#__PURE__*/React.createElement(LinkButton, {
                className: styles['link'],
                to: createLink(undefined, proxy, value, params),
                label: value.name
              });
            } else {
              valueElement = null;
            }

            break;
          }

        case 'downloadFile':
          {
            alignment = alignment !== 'align-right' ? 'align-right' : alignment;

            if (value) {
              valueElement = /*#__PURE__*/React.createElement("div", {
                className: styles['tooltip'],
                style: {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  background: 'transparent',
                  padding: '4px',
                  margin: '2px'
                }
              }, /*#__PURE__*/React.createElement("span", {
                id: `tooltip_${value[1]}`,
                className: styles["tooltiptext"]
              }, i18n`The selected file doesn't exist`), /*#__PURE__*/React.createElement("button", {
                key: "settings",
                className: styles['action-button'],
                onClick: e => {
                  e.stopPropagation();
                  e.preventDefault();
                  downloadFile({
                    filePath: value[0],
                    id: `tooltip_${value[1]}`
                  });
                }
              }, /*#__PURE__*/React.createElement(Icon, {
                name: "download-file"
              })));
            }

            break;
          }

        case 'tooltipDownloadFileError':
          {
            alignment = alignment !== 'align-right' ? 'align-right' : alignment;

            if (value) {
              valueElement = /*#__PURE__*/React.createElement("div", {
                className: styles['tooltip'],
                style: {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  background: 'transparent',
                  padding: '4px',
                  margin: '2px'
                }
              }, /*#__PURE__*/React.createElement("span", {
                id: `tooltip_${value}`,
                className: styles["tooltiptext"]
              }, i18n`The selected file doesn't exist`));
            }

            break;
          }

        case 'deleteFile':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;

            if (((data || []).status === 'invalid' || (data || []).status === 'error') && value && checkRoles(user, [UserRoles.ADMIN, UserRoles.OWNER, UserRoles.PP_SUPPORT, UserRoles.SUPPORT])) {
              valueElement = /*#__PURE__*/React.createElement("div", {
                style: {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  background: 'transparent',
                  padding: '4px',
                  margin: '2px'
                }
              }, /*#__PURE__*/React.createElement("button", {
                key: "settings",
                className: styles['action-button'],
                onClick: e => {
                  e.stopPropagation();
                  e.preventDefault();
                  pushDialog('ConfirmDialog', i18n`Delete File Permanently`, {
                    title: i18n`Are you sure you want to delete permanently the file: ${value[1]}?`,
                    text: i18n`Please confirm that you want to proceed!`
                  }).then(() => {
                    deleteInvalidFilePermanently({
                      projectId: params.projectId,
                      fileId: value[0],
                      userId: user.id
                    });
                  }).catch(() => null);
                }
              }, /*#__PURE__*/React.createElement(Icon, {
                name: "trash-can"
              })));
            } else {
              valueElement = /*#__PURE__*/React.createElement("div", {
                style: {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  background: 'transparent',
                  padding: '4px',
                  margin: '2px',
                  height: '30px'
                }
              });
            }

            break;
          }

        case "deleteFieldDataDictionary":
          {
            alignment = alignment !== "align-right" ? "align-right" : alignment;

            if (value) {
              valueElement = /*#__PURE__*/React.createElement("div", {
                style: {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  background: "transparent",
                  padding: "4px",
                  margin: "2px",
                  height: "35px"
                }
              }, /*#__PURE__*/React.createElement("button", {
                key: "settings",
                className: styles['action-button'],
                onClick: e => {
                  e.stopPropagation();
                  e.preventDefault();
                  pushDialog('ConfirmDialog', i18n`Delete Field Permanently`, {
                    title: i18n`Are you sure you want to delete permanently the file: ${value[0]}?`,
                    text: i18n`Please confirm that you want to proceed!`
                  }).then(() => {
                    deleteDataDictionaryRow({
                      CDMName: value[0],
                      configSet: value[1]
                    }).then(() => {
                      reload && reload();
                    });
                  }).catch(() => null);
                }
              }, /*#__PURE__*/React.createElement(Icon, {
                name: "trash-can"
              })));
            }

            break;
          }

        case "sendSAP":
          {
            alignment = alignment !== "align-right" ? "align-right" : alignment;

            if (value) {
              valueElement = /*#__PURE__*/React.createElement("div", {
                style: {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  background: "transparent",
                  padding: "4px",
                  margin: "2px",
                  height: "35px"
                }
              }, (value[1] === "CANCELED" || value[1] === "SUBMITTED") && /*#__PURE__*/React.createElement("button", {
                key: "settings",
                className: styles["action-button"],
                onClick: e => {
                  e.stopPropagation();
                  e.preventDefault();
                  sendSAP({
                    filePath: value[0],
                    params
                  });
                }
              }, /*#__PURE__*/React.createElement(Icon, {
                name: "refresh"
              })));
            }

            break;
          }

        case "downloadJson":
          {
            alignment = alignment !== "align-right" ? "align-right" : alignment;

            if (value) {
              valueElement = /*#__PURE__*/React.createElement("div", {
                style: {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  background: "transparent",
                  padding: "4px",
                  margin: "2px",
                  height: "35px"
                }
              }, (value[1] === "CANCELED" || value[1] === "SUBMITTED") && /*#__PURE__*/React.createElement("button", {
                key: "settings",
                className: styles["action-button"],
                onClick: e => {
                  e.stopPropagation();
                  e.preventDefault();
                  downloadJSON({
                    filePath: value,
                    params
                  });
                }
              }, /*#__PURE__*/React.createElement(Icon, {
                name: "download-file"
              })));
            }

            break;
          }

        case 'hideFile':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;

            if (value && checkRoles(user, [UserRoles.ADMIN, UserRoles.OWNER, UserRoles.PP_SUPPORT, UserRoles.SUPPORT])) {
              valueElement = /*#__PURE__*/React.createElement("div", {
                style: {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  background: 'transparent',
                  padding: '4px',
                  margin: '2px'
                }
              }, /*#__PURE__*/React.createElement("button", {
                key: "settings",
                className: styles['action-button'],
                onClick: e => {
                  e.stopPropagation();
                  e.preventDefault();
                  pushDialog('ConfirmDialog', i18n`Delete File Permanently`, {
                    title: i18n`Are you sure you want to delete permanently the file?`,
                    text: i18n`Please confirm that you want to proceed!`
                  }).then(() => {
                    hideFile({
                      fileId: value
                    });
                  }).catch(() => null);
                }
              }, /*#__PURE__*/React.createElement(Icon, {
                name: "trash-can"
              })));
            }

            break;
          }

        case 'button_link':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;

            if (value && checkRoles(user, [UserRoles.ADMIN, UserRoles.OWNER, UserRoles.PP_SUPPORT, UserRoles.SUPPORT])) {
              if (value && value.id && value.name) {
                const to = createLink(undefined, proxy, value, params);
                valueElement = /*#__PURE__*/React.createElement("button", {
                  key: "settings",
                  className: styles[`${value.style ? value.style : "action-button"}`],
                  onClick: e => {
                    if (to) {
                      e.preventDefault();
                      e.stopPropagation();

                      if (e.metaKey) {
                        window.open(to, "_blank");
                      } else {
                        history.push(to);
                      }
                    }
                  }
                }, /*#__PURE__*/React.createElement(Icon, {
                  name: `${value.icon ? value.icon : "settings"}`
                }));
              } else {
                valueElement = null;
              }
            }

            break;
          }

        case 'user_actions':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;
            columnWidth = 'actions-width';
            const actions = checkbox && renderRowActions(value || [], (data || []).id, handleChangeCheckbox, (data || []).checked);

            if (checkRoles(user, [UserRoles.ADMIN, UserRoles.OWNER, UserRoles.PP_SUPPORT, UserRoles.SUPPORT, UserRoles.WRITER])) {
              valueElement = /*#__PURE__*/React.createElement("div", {
                className: styles['actions']
              }, actions);
            }

            break;
          }

        case 'taxonomy-status':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;
            valueElement = /*#__PURE__*/React.createElement("div", {
              className: `${styles['align-center']} ${styles[value]}`
            }, /*#__PURE__*/React.createElement("p", null, i18n`${value}`));
            break;
          }

        case 'audit':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;
            valueElement = /*#__PURE__*/React.createElement("div", {
              className: `${styles['align-center']} ${styles[value[0]]}`
            }, /*#__PURE__*/React.createElement("small", null, value[1]));
            break;
          }

        case 'user-status':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;
            valueElement = /*#__PURE__*/React.createElement("div", {
              className: `${styles['align-center']} ${styles['user-status-' + value]}`
            }, /*#__PURE__*/React.createElement("p", null, i18n`${'user-status-' + value}`));
            break;
          }

        case 'custom-status':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;
            valueElement = /*#__PURE__*/React.createElement("div", {
              className: `${styles['align-center']} ${styles[classStyleName]}`
            }, /*#__PURE__*/React.createElement("p", null, i18n`${'status-' + value}`));
            break;
          }

        case 'is-active':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;

            if (value || value !== null) {
              valueElement = /*#__PURE__*/React.createElement("div", {
                className: `${styles['align-center']} ${styles['is-active-' + value]}`
              }, /*#__PURE__*/React.createElement("p", null, i18n`${'is-active-' + value}`));
            } else {
              valueElement = /*#__PURE__*/React.createElement("div", {
                className: `${styles['align-center']} ${styles['is-active-default']}`
              }, /*#__PURE__*/React.createElement("p", null, `-`));
            }

            break;
          }

        case 'dataFileStatus':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;
            let statusClass = '';

            switch (value[0]) {
              case 'invalid':
              case 'error':
                statusClass = `error-status`;
                break;

              case 'imported':
                statusClass = 'ok-status';
                break;

              default:
                statusClass = 'in_progress-status';
                break;
            }

            valueElement = /*#__PURE__*/React.createElement("span", {
              className: `${styles['status-container']} ${styles[statusClass]} ${styles['text_color']}`
            }, /*#__PURE__*/React.createElement("strong", {
              className: `${styles['status']}`
            }, value[1] || "N/A"));
            break;
          }

        case 'stageFlow':
          {
            alignment = alignment !== 'align-center' ? 'align-center' : alignment;
            let status;

            const getFlowType = flowState => {
              let statusFow = undefined;

              switch (flowState) {
                case 'PARSE':
                  statusFow = '2';
                  break;

                case 'KPI':
                  statusFow = '3';
                  break;

                case 'RULES':
                  statusFow = '4';
                  break;

                case 'MERGE':
                  statusFow = '5';
                  break;

                case 'READY':
                  statusFow = '6';
                  break;

                default:
                  break;
              }

              return statusFow;
            };

            switch (value[0]) {
              case 'uploaded':
                status = '1';
                break;

              case 'in_progress':
              case 'valid':
                status = getFlowType(value[2]) || '2';
                break;

              case 'invalid':
                status = getFlowType(value[2]) || '2';
                break;

              case 'error':
                status = getFlowType(value[2]) || '2';
                break;

              case 'imported':
                status = '6';
                break;

              default:
                status = '0';
                break;
            }

            valueElement = /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("strong", {
              className: `${styles['status']}`
            }, status), "/6"));
            break;
          }

        default:
          {
            alignment = 'align-left';
            valueElement = value && value != 'null' || value == 0 ? value : "N/A";
            break;
          }
      }

      const Tag = type === 'object' || type === 'user_actions' || type === 'periods' ? 'div' : style === 'bold' ? 'h5' : 'small';
      return /*#__PURE__*/React.createElement(Tag, {
        key: rowIndex,
        className: `
					${align ? `align-${align}` : alignment} 
					${mobileHidden ? '' : styles['mobile']}
					${styles[style] || ''}
				`
      }, valueElement);
    });
    const mobileHidden = column.every(col => (col || {}).mobileHidden);

    if (!checkbox && columnWidth === 'actions-width') {
      return;
    }

    return /*#__PURE__*/React.createElement("span", {
      key: index,
      className: `${styles['column']} ${styles[columnWidth] || ''} ${mobileHidden ? '' : styles['mobile']}`,
      style: flexBasis && {
        flexBasis
      }
    }, rows);
  });
  return /*#__PURE__*/React.createElement("div", {
    className: `
				${styles['row']} ${rowStatus || ''}
				${data.__typename === "DataFile" && data.status === 'deleted' && styles['error-line']}
				${data.__typename === "DataFile" && data.status === 'imported' && styles['success-line']}
				${/[a-zA-Z]*FindingSummary|[a-zA-Z]*Finding/.test(data.__typename) && styles[`severity-${data.severity}-border`]}
				${data.__typename === "Project" && data.active && styles['success-line']}
				${data.__typename === "Project" && !data.active && styles['disabled-line']}
			`
  }, columns);
};

export default EntityCard;